import { useCallback, useEffect, useState } from 'react';

import { api, type MenuLeiro, type MuveletLeiro } from '../api.ts';
import { useFelhasznalo, useJog } from '../felhasznalo-context.ts';
import { huRendez } from '../formazas.ts';

/**
 * Felhasználók és szerepkörök. (3.0 / 1.)
 *
 * Két képernyő egy fájlban, mert együtt járnak: a felhasználó egy
 * szerepkört kap, a szerepkör pedig azt mondja meg, mit lát és mit tehet.
 *
 * **A jogosultságot a szerver tartatja be**, ez csak a beállítás felülete.
 * A gyári szerepkörök jogai **átírhatók** — nem kell másolatot csinálni
 * egyetlen apróság kedvéért. Egyedül az adminisztrátoré védett: tőle nem
 * vehető el a jog, különben ki lehetne zárni magatokat a rendszerből.
 * Törölni viszont csak a saját készítésű szerepköröket lehet.
 */

interface FelhasznaloSor {
  id: number;
  nev: string;
  felhasznalonev: string;
  szerepkor_id: number;
  szerepkor_nev: string;
  admin: boolean;
  sofor_id: number | null;
  sofor_nev: string | null;
  rendszam: string | null;
  email: string | null;
  aktiv: boolean;
  van_alairas: boolean;
  utolso_belepes: string | null;
}

interface SzerepkorSor {
  id: number;
  nev: string;
  rendszer: boolean;
  admin: boolean;
  jogok: Record<string, number>;
  felhasznalok: number;
}

interface SoforSor {
  id: number;
  nev: string;
  aktiv: boolean;
}

const JOGSZINT = ['nem látja', 'látja', 'új felvétel', 'módosíthat'];

export function Felhasznalok({ kezdoFul }: { kezdoFul?: 'felhasznalok' | 'szerepkorok' }) {
  const en = useFelhasznalo();
  const felhJog = useJog('felh');
  const szerepJog = useJog('szerep');

  const [ful, setFul] = useState<'felhasznalok' | 'szerepkorok'>(kezdoFul ?? 'felhasznalok');
  const [sorok, setSorok] = useState<FelhasznaloSor[]>([]);
  const [szerepek, setSzerepek] = useState<SzerepkorSor[]>([]);
  const [soforok, setSoforok] = useState<SoforSor[]>([]);
  const [menuk, setMenuk] = useState<MenuLeiro[]>([]);
  const [muveletek, setMuveletek] = useState<MuveletLeiro[]>([]);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);

  /** Melyik szerepkört szerkesztjük. */
  const [aktivSzerep, setAktivSzerep] = useState<number | null>(null);
  /** Új felhasználó űrlapja. */
  const [ujUrlap, setUjUrlap] = useState(false);
  const [uj, setUj] = useState({ nev: '', felhasznalonev: '', jelszo: '', szerepkor_id: 0, sofor_id: '' });
  /** Kinek állítunk új jelszót, és mire. */
  const [jelszoSor, setJelszoSor] = useState<FelhasznaloSor | null>(null);
  const [ujJelszo, setUjJelszo] = useState('');
  /** Kinek a törlését kérdezzük vissza. */
  const [torles, setTorles] = useState<FelhasznaloSor | null>(null);
  const [szerepTorles, setSzerepTorles] = useState<SzerepkorSor | null>(null);
  /*
   * A szerepkör neve külön, gombbal mentődik. Korábban a mezőből kilépve
   * mentett magától, de ha valaki a név beírása után rögtön egy jogra
   * kattintott, a két kérés versenyzett, és a név visszaugrott a régire —
   * kívülről ez úgy látszott, mintha nem lehetne elnevezni. (2026-09-28)
   */
  const [ujNev, setUjNev] = useState<string | null>(null);

  const betolt = useCallback(async () => {
    try {
      const [f, sz, s, e] = await Promise.all([
        api.get<{ sorok: FelhasznaloSor[] }>('/api/felhasznalo'),
        api.get<{ sorok: SzerepkorSor[] }>('/api/szerepkor'),
        api.get<{ sorok: SoforSor[] }>('/api/sofor'),
        api.get<{ menuk: MenuLeiro[]; muveletek: MuveletLeiro[] }>('/api/en'),
      ]);
      setSorok(f.sorok);
      setSzerepek(sz.sorok);
      setSoforok(s.sorok.filter((x) => x.aktiv));
      setMenuk(e.menuk);
      setMuveletek(e.muveletek);
      setAktivSzerep((a) => a ?? sz.sorok[0]?.id ?? null);
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az adatokat nem sikerült betölteni.');
    }
  }, []);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  async function mezo(sor: FelhasznaloSor, mezo: string, ertek: unknown) {
    try {
      await api.patch(`/api/felhasznalo/${sor.id}/mezo/${mezo}`, { ertek });
      await betolt();
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A módosítás nem sikerült.');
      await betolt();
    }
  }

  async function ujFelhasznalo() {
    try {
      await api.post('/api/felhasznalo', {
        nev: uj.nev,
        felhasznalonev: uj.felhasznalonev,
        jelszo: uj.jelszo,
        szerepkor_id: uj.szerepkor_id || szerepek[szerepek.length - 1]?.id,
        sofor_id: uj.sofor_id ? Number(uj.sofor_id) : null,
      });
      setUjUrlap(false);
      setUj({ nev: '', felhasznalonev: '', jelszo: '', szerepkor_id: 0, sofor_id: '' });
      setUzenet('Az új felhasználó felvéve. A jelszavát az első belépés után cserélje le.');
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A felhasználót nem sikerült felvenni.');
    }
  }

  async function jelszotAllit() {
    if (!jelszoSor) return;
    try {
      await api.post(`/api/felhasznalo/${jelszoSor.id}/jelszo`, { jelszo: ujJelszo });
      setUzenet(`${jelszoSor.nev} új jelszót kapott. Add meg neki, és cseréltesd le vele.`);
      setJelszoSor(null);
      setUjJelszo('');
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A jelszót nem sikerült beállítani.');
    }
  }

  async function felhasznalotTorol() {
    if (!torles) return;
    try {
      await api.torol(`/api/felhasznalo/${torles.id}`);
      setTorles(null);
      setUzenet('A felhasználó törölve. Amit eddig csinált, a naplóban megmarad.');
      await betolt();
    } catch (h) {
      setTorles(null);
      setHiba(h instanceof Error ? h.message : 'A törlés nem sikerült.');
    }
  }

  /* --------------------------- szerepkörök --------------------------- */

  const szerep = szerepek.find((s) => s.id === aktivSzerep) ?? null;

  async function jogot(kulcs: string, szint: number) {
    if (!szerep) return;
    try {
      const v = await api.patch<SzerepkorSor>(`/api/szerepkor/${szerep.id}/jog/${kulcs}`, { szint });
      setSzerepek((lista) => lista.map((s) => (s.id === v.id ? v : s)));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A jogosultságot nem sikerült átírni.');
      await betolt();
    }
  }

  async function szerepetAtnevez(nev: string) {
    if (!szerep) return;
    try {
      const v = await api.patch<SzerepkorSor>(`/api/szerepkor/${szerep.id}/nev`, { nev });
      setSzerepek((lista) => lista.map((s) => (s.id === v.id ? v : s)));
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Az átnevezés nem sikerült.');
      await betolt();
    }
  }

  async function atnevez() {
    if (!szerep || ujNev === null) return;
    const tiszta = ujNev.trim();
    if (!tiszta || tiszta === szerep.nev) {
      setUjNev(null);
      return;
    }
    await szerepetAtnevez(tiszta);
    setUjNev(null);
    setUzenet('A szerepkör új nevet kapott.');
  }

  async function ujSzerepkor(masolt: boolean) {
    try {
      const v = await api.post<SzerepkorSor>('/api/szerepkor', {
        nev: masolt && szerep ? `${szerep.nev} másolat` : 'Új szerepkör',
        masolt_id: masolt ? szerep?.id : null,
      });
      await betolt();
      setAktivSzerep(v.id);
      setUjNev(v.nev);
      setUzenet(
        masolt
          ? 'Elkészült a másolat — ezen már szabadon átállíthatod a jogokat.'
          : 'Új szerepkör. Alapból semmit nem lát; kapcsold be, amit szabad neki.',
      );
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A szerepkört nem sikerült létrehozni.');
    }
  }

  async function szerepkortTorol() {
    if (!szerepTorles) return;
    try {
      await api.torol(`/api/szerepkor/${szerepTorles.id}`);
      setSzerepTorles(null);
      setAktivSzerep(null);
      await betolt();
      setUzenet('A szerepkör törölve.');
    } catch (h) {
      setSzerepTorles(null);
      setHiba(h instanceof Error ? h.message : 'A törlés nem sikerült.');
    }
  }

  /* ------------------------------ rajz ------------------------------- */

  const modosithatFelh = felhJog > 2;
  const ujFelvetel = felhJog > 1;
  const modosithatSzerep = szerepJog > 2;
  const torolhet = !!en?.admin;

  /** A menüpontok a saját csoportjuk szerint, a szerver sorrendjében. */
  const csoportok: Array<[string, MenuLeiro[]]> = [];
  for (const m of menuk) {
    const utolso = csoportok[csoportok.length - 1];
    if (utolso && utolso[0] === m[2]) utolso[1].push(m);
    else csoportok.push([m[2], [m]]);
  }

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Felhasználók és jogosultságok</span>
        <div className="vsw">
          <button
            className={ful === 'felhasznalok' ? 'on' : ''}
            onClick={() => setFul('felhasznalok')}
          >
            Felhasználók
          </button>
          <button className={ful === 'szerepkorok' ? 'on' : ''} onClick={() => setFul('szerepkorok')}>
            Szerepkörök
          </button>
        </div>
        <span className="hint">
          {ful === 'felhasznalok'
            ? 'A belépéshez felhasználónév és jelszó kell. E-mail nem kötelező.'
            : 'Menüpontonként: nem látja · látja · új felvétel · módosíthat'}
        </span>
        <div className="stats">
          <div>
            Aktív<strong>{sorok.filter((s) => s.aktiv).length}</strong>
          </div>
        </div>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      {ful === 'felhasznalok' ? (
        <>
          <div className="actbar" style={{ marginTop: 14 }}>
            {ujFelvetel && (
              <button className="btn pri sm" onClick={() => setUjUrlap(!ujUrlap)}>
                + Új felhasználó
              </button>
            )}
            <span className="hint">
              Elfelejtett jelszónál az adminisztrátor állít újat — a régit senki nem látja.
            </span>
          </div>

          {ujUrlap && (
            <div className="npform" style={{ maxWidth: 560, marginTop: 12 }}>
              <h4>Új felhasználó</h4>
              <div className="tetelmezok">
                <label>
                  Név
                  <input
                    autoFocus
                    value={uj.nev}
                    onChange={(e) => setUj({ ...uj, nev: e.target.value })}
                  />
                </label>
                <label>
                  Felhasználónév
                  <input
                    value={uj.felhasznalonev}
                    onChange={(e) => setUj({ ...uj, felhasznalonev: e.target.value })}
                  />
                </label>
                <label>
                  Jelszó
                  <input
                    value={uj.jelszo}
                    onChange={(e) => setUj({ ...uj, jelszo: e.target.value })}
                  />
                </label>
                <label>
                  Szerepkör
                  <select
                    value={uj.szerepkor_id || ''}
                    onChange={(e) => setUj({ ...uj, szerepkor_id: Number(e.target.value) })}
                  >
                    <option value="">— válassz —</option>
                    {szerepek.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nev}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Sofőr (ha sofőr)
                  <select
                    value={uj.sofor_id}
                    onChange={(e) => setUj({ ...uj, sofor_id: e.target.value })}
                  >
                    <option value="">—</option>
                    {soforok.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nev}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="btns">
                <button
                  className="btn pri sm"
                  disabled={!uj.nev.trim() || !uj.felhasznalonev.trim() || uj.jelszo.length < 8}
                  onClick={() => void ujFelhasznalo()}
                >
                  Felvétel
                </button>
                <button className="btn sm" onClick={() => setUjUrlap(false)}>
                  Mégse
                </button>
                <span className="hint">A jelszó legalább 8 karakter legyen.</span>
              </div>
            </div>
          )}

          <div className="matrix" style={{ marginTop: 12 }}>
            <table className="tt2 fhtabla">
              <thead>
                <tr>
                  <th>Név</th>
                  <th>Felhasználónév</th>
                  <th>Szerepkör</th>
                  <th>Sofőr</th>
                  <th>E-mail</th>
                  <th>Aláírás</th>
                  <th className="n">Utolsó belépés</th>
                  <th style={{ width: 54, textAlign: 'center' }}>Aktív</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorok.map((s) => (
                  <tr key={s.id} className={s.aktiv ? undefined : 'nul'}>
                    <td className="nm3">
                      <input
                        className="txt"
                        defaultValue={s.nev}
                        disabled={!modosithatFelh}
                        onBlur={(e) => e.target.value !== s.nev && void mezo(s, 'nev', e.target.value)}
                      />
                    </td>
                    <td className="calc2">
                      <input
                        className="txt"
                        defaultValue={s.felhasznalonev}
                        disabled={!modosithatFelh}
                        onBlur={(e) =>
                          e.target.value !== s.felhasznalonev &&
                          void mezo(s, 'felhasznalonev', e.target.value)
                        }
                      />
                    </td>
                    <td className="sel">
                      <select
                        value={s.szerepkor_id}
                        disabled={!modosithatFelh}
                        onChange={(e) => void mezo(s, 'szerepkor_id', Number(e.target.value))}
                      >
                        {szerepek.map((sz) => (
                          <option key={sz.id} value={sz.id}>
                            {sz.nev}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="sel">
                      <select
                        value={s.sofor_id ?? ''}
                        disabled={!modosithatFelh}
                        onChange={(e) =>
                          void mezo(s, 'sofor_id', e.target.value ? Number(e.target.value) : null)
                        }
                      >
                        <option value="">—</option>
                        {soforok.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.nev}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="txt"
                        defaultValue={s.email ?? ''}
                        placeholder="nem kötelező"
                        disabled={!modosithatFelh}
                        onBlur={(e) =>
                          e.target.value !== (s.email ?? '') && void mezo(s, 'email', e.target.value)
                        }
                      />
                    </td>
                    <td className="c">{s.van_alairas ? 'van' : '—'}</td>
                    <td className="calc2">{s.utolso_belepes?.slice(0, 16).replace('T', ' ') ?? '—'}</td>
                    <td className="c">
                      <input
                        type="checkbox"
                        checked={s.aktiv}
                        disabled={!modosithatFelh}
                        onChange={(e) => void mezo(s, 'aktiv', e.target.checked)}
                      />
                    </td>
                    <td className="act" style={{ whiteSpace: 'nowrap' }}>
                      {(en?.admin || en?.id === s.id) && (
                        <button
                          className="btn sm"
                          onClick={() => {
                            setJelszoSor(s);
                            setUjJelszo('');
                          }}
                        >
                          Jelszó
                        </button>
                      )}
                      {torolhet && s.id !== en?.id && (
                        <button className="xb" title="Törlés" onClick={() => setTorles(s)}>
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {sorok.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                      Még nincs felhasználó.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {jelszoSor && (
            <div className="npform" style={{ maxWidth: 480, marginTop: 12 }}>
              <h4>Új jelszó — {jelszoSor.nev}</h4>
              <div className="c" style={{ marginBottom: 8 }}>
                A régi jelszót senki nem tudja visszanézni, csak újat lehet adni. Add meg neki
                személyesen, és kérd meg, hogy lépés után cserélje le a sajátjára.
              </div>
              <input
                autoFocus
                value={ujJelszo}
                placeholder="legalább 8 karakter"
                onChange={(e) => setUjJelszo(e.target.value)}
              />
              <div className="btns">
                <button
                  className="btn pri sm"
                  disabled={ujJelszo.length < 8}
                  onClick={() => void jelszotAllit()}
                >
                  Beállítás
                </button>
                <button className="btn sm" onClick={() => setJelszoSor(null)}>
                  Mégse
                </button>
              </div>
            </div>
          )}

          {torles && (
            <div className="npform" style={{ maxWidth: 480, marginTop: 12 }}>
              <h4>Törlés — {torles.nev}</h4>
              <div className="c" style={{ marginBottom: 8 }}>
                Amit eddig csinált, a naplóban megmarad. Ha csak arról van szó, hogy már nem
                dolgozik itt, <b>inaktívvá tenni jobb</b>: úgy a régi bejegyzései mellett a neve is
                értelmes marad.
              </div>
              <div className="btns">
                <button className="btn sm torlo" onClick={() => void felhasznalotTorol()}>
                  Igen, törlöm
                </button>
                <button className="btn sm" onClick={() => setTorles(null)}>
                  Mégse
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="vsw" style={{ marginTop: 12 }}>
            {[...szerepek]
              .sort((a, b) => huRendez(a.nev, b.nev))
              .map((s) => (
                <button
                  key={s.id}
                  className={s.id === aktivSzerep ? 'on' : ''}
                  onClick={() => {
                    setAktivSzerep(s.id);
                    setUjNev(null);
                  }}
                >
                  {s.nev}
                  <b>{s.felhasznalok}</b>
                </button>
              ))}
          </div>

          <div className="actbar" style={{ marginTop: 12 }}>
            {szerepJog > 1 && (
              <>
                <button className="btn sm" onClick={() => void ujSzerepkor(false)}>
                  + Új szerepkör
                </button>
                <button className="btn sm" disabled={!szerep} onClick={() => void ujSzerepkor(true)}>
                  Másolat készítése
                </button>
              </>
            )}
            {torolhet && szerep && !szerep.rendszer && (
              <button className="btn sm torlo" onClick={() => setSzerepTorles(szerep)}>
                Szerepkör törlése
              </button>
            )}
            <span className="hint">
              {szerep?.admin
                ? 'Az adminisztrátor jogai nem korlátozhatók — különben ki lehetne zárni magatokat a rendszerből.'
                : 'A módosítás azonnal érvényes minden ilyen szerepkörű felhasználóra.'}
            </span>
          </div>

          {szerepTorles && (
            <div className="npform" style={{ maxWidth: 480, marginTop: 12 }}>
              <h4>Szerepkör törlése — {szerepTorles.nev}</h4>
              <div className="c" style={{ marginBottom: 8 }}>
                {szerepTorles.felhasznalok > 0
                  ? `${szerepTorles.felhasznalok} felhasználó tartozik ide — előbb rakd át őket máshová.`
                  : 'Nem tartozik hozzá felhasználó, nyugodtan törölhető.'}
              </div>
              <div className="btns">
                <button className="btn sm torlo" onClick={() => void szerepkortTorol()}>
                  Igen, törlöm
                </button>
                <button className="btn sm" onClick={() => setSzerepTorles(null)}>
                  Mégse
                </button>
              </div>
            </div>
          )}

          {szerep && (
            <div className="matrix" style={{ marginTop: 12 }}>
              <table className="tt2">
                <thead>
                  <tr>
                    <th style={{ width: 240 }}>Szerepkör neve</th>
                    <th>
                      <div className="nevsor">
                        <input
                          className="txt"
                          style={{ fontSize: 13, fontWeight: 600 }}
                          value={ujNev ?? szerep.nev}
                          disabled={szerep.admin || !modosithatSzerep}
                          onChange={(e) => setUjNev(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void atnevez();
                            if (e.key === 'Escape') setUjNev(null);
                          }}
                        />
                        {ujNev !== null && ujNev.trim() && ujNev !== szerep.nev && (
                          <>
                            <button className="btn pri sm" onClick={() => void atnevez()}>
                              Átnevezés
                            </button>
                            <button className="btn sm" onClick={() => setUjNev(null)}>
                              Mégse
                            </button>
                          </>
                        )}
                      </div>
                    </th>
                  </tr>
                </thead>
              </table>

              <table className="tt2">
                <thead>
                  <tr>
                    <th>Menüpont</th>
                    <th style={{ width: 340 }}>Jogosultság</th>
                  </tr>
                </thead>
                <tbody>
                  {csoportok.map(([csoport, pontok]) => (
                    <FejlecCsoport key={csoport} cimke={csoport}>
                      {pontok.map(([kulcs, cimke]) => (
                        <tr key={kulcs}>
                          <td className="nm3">
                            <b>{cimke}</b>
                          </td>
                          <td>
                            <div className="jogv">
                              {JOGSZINT.map((szoveg, szint) => (
                                <button
                                  key={szint}
                                  className={`jogb${(szerep.jogok[kulcs] ?? 0) === szint ? ` on${szint}` : ''}`}
                                  disabled={szerep.admin || !modosithatSzerep}
                                  onClick={() => void jogot(kulcs, szint)}
                                >
                                  {szoveg}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </FejlecCsoport>
                  ))}

                  <tr>
                    <td colSpan={2} className="jogcs">
                      Külön engedélyhez kötött műveletek
                    </td>
                  </tr>
                  {muveletek.map(([kulcs, cimke, csak]) => {
                    const csakAdmin = csak === 'admin';
                    return (
                      <tr key={kulcs} className={csakAdmin && !szerep.admin ? 'nul' : undefined}>
                        <td className="nm3">
                          <b>{cimke}</b>
                          {csakAdmin && <small className="only">csak adminisztrátor</small>}
                        </td>
                        <td>
                          <label className="sw">
                            <input
                              type="checkbox"
                              checked={csakAdmin ? szerep.admin : (szerep.jogok[kulcs] ?? 0) > 0}
                              disabled={szerep.admin || !modosithatSzerep || csakAdmin}
                              onChange={(e) => void jogot(kulcs, e.target.checked ? 1 : 0)}
                            />
                            engedélyezve
                          </label>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className={szerep.admin ? undefined : 'nul'}>
                    <td className="nm3">
                      <b>Törlés bármely adatnál</b>
                      <small className="only">csak adminisztrátor</small>
                    </td>
                    <td>
                      <label className="sw">
                        <input type="checkbox" checked={szerep.admin} disabled />
                        engedélyezve
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

/** Csoportfejléc a jogosultság-táblában, alatta a csoport menüpontjaival. */
function FejlecCsoport({ cimke, children }: { cimke: string; children: React.ReactNode }) {
  return (
    <>
      <tr>
        <td colSpan={2} className="jogcs">
          {cimke}
        </td>
      </tr>
      {children}
    </>
  );
}
